import React, { useState, useEffect, useMemo, useContext } from "react";
import { Tooltip } from "@mui/material";
import { useHistory } from "react-router";

import { ReactComponent as InfoIcon } from "assets/img/svg/infoIcon.svg";
import { EyeIcon } from "../../studentFinancials/paymentCollection/manage-receipts/EyeIcon";

import { PermissionContext } from "appContext";
import APopover from "views/pages/manage/common/a-popover";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import TransferTimeline from "modules/studentView/progressTimeLine";
import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";
import { LastUpdatedDetails } from "views/pages/manage/employeeManagement/userProfile/helper";

import {
  editTransactionStatus,
  STATUS_BADGE,
  isActionEnabledForVendorRow,
  getApprovalStepperSteps,
  fetchApprovalJourney,
  FILTERS_DEFAULT_STATE,
} from "./helper";
import { getRoleId } from "modules/studentTransfer/transferApproval/utils";
import {
  approvalLevelMap,
  rejectionLevelMap,
  fetchVendorLevelsByRole,
  fetchApprovalLevelDetails,
  getMaxApproverLevel,
} from "../admission-advisor-dashboard/utils";
import RejectDialog from "./RejectDialog";
import ApproveDialog from "./ApproveDialog";

const CreationApprovalTable = ({
  rows,
  rowCount,
  pagination,
  onPageChange,
  onPageSizeChange,
  setLoading,
  showApproverActions,
  fetchTableData,
}) => {
  const history = useHistory();
  const userRole = useContext(PermissionContext);
  const [vendorLevels, setVendorLevels] = useState({});

  useEffect(() => {
    if (userRole) {
      setRoleId();
    }
  }, [userRole]);

  const [rejectionDialog, setRejectionDialog] = useState({
    isOpen: false,
    data: null,
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    data: null,
  });

  const [steps, setSteps] = useState([]);

  const setRoleId = async () => {
    const roleId = await getRoleId(userRole.roleType);

    const res = await fetchVendorLevelsByRole(roleId);
    setVendorLevels(res || {});
  };

  const getTrackerTimeLine = async (referenceNumber, vendorRes) => {
    setLoading(true);
    const res = await fetchApprovalJourney(referenceNumber);
    const reqObj = {
      businessAreaIds: vendorRes?.businessAreaId
        ? [vendorRes?.businessAreaId]
        : [],
      yearIds: vendorRes?.academicYearId ? [vendorRes?.academicYearId] : [],
      vendorTypes: ["VENDOR_CREATION"], // In  case of vendor crearion, the type will be fixed as "VENDOR_CREATION" as of now.
    };
    const resp = await fetchApprovalLevelDetails(reqObj);
    let level = 0;
    if (resp?.code === 200) {
      level = getMaxApproverLevel(resp?.data?.data?.[0], res?.auditLogsList);
    }
    const logs = res?.auditLogsList || [];
    const formattedSteps = getApprovalStepperSteps(logs, level || 4);
    setSteps(formattedSteps);
    setLoading(false);
  };

  const handleRejectionStatus = async (remark) => {
    setLoading(true);
    const approvalStatus = rejectionLevelMap[rejectionDialog?.data?.status];
    const response = await editTransactionStatus(rejectionDialog?.data.id, {
      approvalStatus: approvalStatus || "L1_REJECTED",
      approvalRemarks: remark,
    });

    if (response?.code === 200) {
      setRejectionDialog({
        isOpen: false,
        data: null,
      });
      setConfirmDialog({
        isOpen: true,
        data: {
          ...rejectionDialog?.data,
          ...response.data,
          remark,
          approvalStatus,
        },
      });

      fetchTableData(0, FILTERS_DEFAULT_STATE);
    }

    setLoading(false);
  };

  const handleApproveClick = async (rowData) => {
    setLoading(true);
    const approvalStatus = approvalLevelMap[rowData.status];
    const response = await editTransactionStatus(rowData.id, {
      approvalStatus,
    });

    if (response?.code === 200) {
      setConfirmDialog({
        isOpen: true,
        data: { ...rowData, ...response.data, approvalStatus },
      });

      fetchTableData(0, FILTERS_DEFAULT_STATE);
    }
    setLoading(false);
  };

  const columns = useMemo(
    () => [
      {
        key: "vendorType",
        label: "Vendor Type",
        width: 100,
        sortable: true,
      },
      {
        key: "vendorSapCode",
        label: "SAP Vendor Id",
        width: 100,
        sortable: true,
        render: (value) => {
          return <span>{value || "-"}</span>;
        },
      },
      {
        key: "panNumber",
        label: "PAN",
        width: 80,
        sortable: true,
      },

      {
        key: "vendorName",
        label: "Name",
        width: 80,
        sortable: true,
      },
      {
        key: "sapPlantId",
        label: "SAP Plant Id",
        width: 100,
        sortable: true,
      },

      {
        key: "businessAreaValue",
        label: "BU Code",
        width: 90,
        sortable: true,
      },
      {
        key: "occupation",
        label: "Job Details",
        width: 100,
        sortable: true,
        render: (value) => {
          return <span>{value || "-"}</span>;
        },
      },
      {
        key: "createdOn",
        label: "Created On/By",
        width: 180,
        sortable: true,
        render: (value, row) => {
          return <span>{LastUpdatedDetails(value, row.createdBy)}</span>;
        },
      },
      {
        key: "updatedOn",
        label: "Last Updated On/By",
        width: 180,
        sortable: true,
        render: (value, row) => {
          return <span>{LastUpdatedDetails(value, row.updatedBy)}</span>;
        },
      },
      {
        key: "status",
        label: "Status",
        pinned: true,
        width: 150,
        align: "right",
        sortable: false,
        render: (value, row) => {
          return (
            <div>
              {STATUS_BADGE(value)}

              <APopover
                buttonComponent={(onClick, styles) => (
                  <Tooltip title="View approval journey">
                    <span
                      onClick={(e) => {
                        onClick(e);
                        getTrackerTimeLine(row?.id, row);
                      }}
                      style={{ cursor: "pointer" }}
                      className="ml-2"
                    >
                      <InfoIcon />
                    </span>
                  </Tooltip>
                )}
                menuComponent={(closeForm) => (
                  <TransferTimeline
                    steps={steps}
                    heading="Creation request status"
                    marginLeft="0px"
                    marginBottom="0px"
                    closeForm={closeForm}
                  />
                )}
              />
            </div>
          );
        },
      },

      {
        key: "actions",
        label: "Actions",
        pinned: true,
        width: showApproverActions ? "230" : "80",
        align: "right",
        sortable: false,
        render: (value, row) => {
          const enabled = isActionEnabledForVendorRow(
            row,
            userRole.roleType,
            vendorLevels,
          );
          return (
            <div>
              {showApproverActions && (
                <>
                  <AButton
                    variant="danger_filled"
                    size="xs"
                    className="no-ml-btn"
                    onClick={() => {
                      setRejectionDialog({ isOpen: true, data: row });
                    }}
                    disabled={!enabled}
                  >
                    Reject
                  </AButton>
                  <AButton
                    variant="primary_filled"
                    size="xs"
                    onClick={() => {
                      handleApproveClick(row);
                    }}
                    disabled={!enabled}
                  >
                    Approve
                  </AButton>
                </>
              )}
              <Tooltip title="More details">
                <span
                  onClick={() =>
                    history.push(`/admin/vendorInfo/view/${row.id}`)
                  }
                  style={{ cursor: "pointer" }}
                  className="ml-2"
                >
                  <EyeIcon />
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [vendorLevels, steps],
  );

  return (
    <>
      <ServerDataGrid
        rows={rows}
        columns={columns}
        rowKey="id" // Ensure your data has a unique 'id' field, or change this
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectable={false}
        height="500px"
      />

      {rejectionDialog.isOpen && (
        <RejectDialog
          isOpen={rejectionDialog.isOpen}
          setRejectionDialog={setRejectionDialog}
          rejectionHandler={handleRejectionStatus}
        />
      )}

      {confirmDialog.isOpen && (
        <ApproveDialog
          setConfirmDialog={setConfirmDialog}
          confirmDialog={confirmDialog}
        />
      )}
    </>
  );
};

export default CreationApprovalTable;
